import type { FC, ReactNode } from 'react';

import { Body } from '../components/Body';
import { Footer } from '../components/Footer';
import { Header } from '../components/Header';
import Root from '../components/Root';

type DiscardChangesModalProps = {
  isOpen: boolean;
  onKeepEditing: () => void;
  onDiscardChanges: () => void;
  title?: string;
  children?: ReactNode;
  /** @default 'Keep changes' */
  keepEditingLabel?: string;
  /** @default 'Discard changes' */
  discardChangesLabel?: string;
  /**
   * Shows the destructive action as loading and locks every exit: the header
   * close button is hidden, Escape is suppressed, the safe action is
   * disabled, and the destructive action itself becomes disabled and
   * untabbable too (`@latitude/button` maps `loading` onto `disabled` and
   * `tabIndex={-1}`). Nothing in the dialog is clickable or reachable by
   * keyboard while this is true, so the caller must clear it on both success
   * and failure of `onDiscardChanges` — otherwise the dialog is stuck with no
   * way to dismiss it.
   *
   * `ModalProps.loading` is a different contract: it only overlays a backdrop
   * on the body and leaves the footer actions and close button live.
   * @default false
   */
  isDiscarding?: boolean;
  'data-testid'?: string;
};

/**
 * Composes the sub-components rather than `Modal`, which lives in the package
 * barrel: importing it here would make the barrel depend on this file and this
 * file on the barrel.
 *
 * Outside-click is deliberately left inert: this prompt exists because the user
 * has unrecoverable work in progress, so a stray click must not be the thing
 * that destroys it. Only the destructive action runs `onDiscardChanges`; every
 * other exit — the header close button, Escape, the safe action — runs
 * `onKeepEditing` and preserves the work.
 *
 * Action labels are overridable; their variants are not. The safe way out must
 * never carry a brand colour, so nothing on the dialog reads as recommended.
 *
 * While `isDiscarding`, every exit is locked, including the destructive
 * action itself — it shows a spinner but is disabled and untabbable like the
 * rest of the dialog. This is a one-way door: the caller owns clearing
 * `isDiscarding` on both success and failure of `onDiscardChanges`, or the
 * dialog can't be dismissed by any input at all.
 */
const DiscardChangesModal: FC<DiscardChangesModalProps> = ({
  isOpen,
  onKeepEditing,
  onDiscardChanges,
  title = 'Discard unsaved changes?',
  children = 'If you leave now, your progress will be lost.',
  keepEditingLabel = 'Keep changes',
  discardChangesLabel = 'Discard changes',
  isDiscarding = false,
  'data-testid': testId = 'discard-changes-modal',
}) => (
  <Root
    isOpen={isOpen}
    onClose={onKeepEditing}
    size="small"
    data-testid={testId}
    onEscapeKeyDown={(e) => {
      if (isDiscarding) {
        e.preventDefault();
      }
    }}
  >
    <Header
      title={title}
      onClose={onKeepEditing}
      hideCloseButton={isDiscarding}
    />
    <Body>{children}</Body>
    <Footer
      footerProps={{
        primaryAction: {
          label: keepEditingLabel,
          variant: 'neutral',
          type: 'outline',
          onClick: onKeepEditing,
          disabled: isDiscarding,
        },
        secondaryAction: {
          label: discardChangesLabel,
          variant: 'danger',
          type: 'filled',
          onClick: onDiscardChanges,
          loading: isDiscarding,
        },
      }}
    />
  </Root>
);

DiscardChangesModal.displayName = 'DiscardChangesModal';

export { DiscardChangesModal, type DiscardChangesModalProps };
